/**
 * The site's own page-transition animation, played after a successful booking.
 *
 * `window.cdPreloader.play()` is a global the theme defines; it returns a promise that resolves
 * when the animation FINISHES (~4.2s on this site, measured 2026-09-09), so the caller can wait
 * for it rather than guessing a delay. Everything here is defensive: a customer who has paid must
 * never be stranded because an animation is missing, throws, or never finishes.
 */
declare global {
  interface Window {
    cdPreloader?: { play?: () => unknown; stop?: () => unknown };
  }
}

/** Longest we will hold a paid customer waiting for the animation. */
export const PRELOADER_CAP_MS = 8000;

export type PreloaderOutcome = 'played' | 'timeout' | 'absent' | 'failed';

export function playSitePreloader(
  win: Window | undefined = typeof window === 'undefined' ? undefined : window,
  capMs = PRELOADER_CAP_MS,
): Promise<PreloaderOutcome> {
  const play = win?.cdPreloader?.play;
  if (typeof play !== 'function') return Promise.resolve('absent');
  let result: unknown;
  try {
    result = play.call(win!.cdPreloader);
  } catch {
    return Promise.resolve('failed');
  }
  const done: Promise<PreloaderOutcome> = Promise.resolve(result).then(
    () => 'played',
    () => 'failed',
  );
  return new Promise((resolve) => {
    const timer = win!.setTimeout(() => resolve('timeout'), capMs);
    void done.then((outcome) => {
      win!.clearTimeout(timer);
      resolve(outcome);
    });
  });
}
